import { useRef, useState } from 'react';
import { ElevationRequests } from '@/components/access/elevation-requests';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ProfileAccess } from '@/components/access/profile-access';
import { ElevateAccessDrawer } from '@/components/access/temporary-access';
import {
  BellIcon,
  CircleHelpIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  Settings2Icon,
  SunMoonIcon,
  SunIcon
} from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/components/auth/user-provider';
import { useTheme, type Theme } from '@/components/theme/theme-provider';
import { useManageMode } from '@/components/manage/manage-mode';

export function ProfileMenu() {
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [elevateOpen, setElevateOpen] = useState(false);
  const [panel, setPanel] = useState<'notifications' | 'help' | null>(null);
  const { session, signOut } = useSession();
  const { theme, setTheme } = useTheme();
  const { enabled, setEnabled } = useManageMode();

  if (session.status !== 'signedIn') return null;
  const { user } = session;

  return (
    <>
    <DropdownMenu dir="rtl" open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button ref={trigger} variant="ghost" size="icon" className="rounded-full" aria-label="Open profile menu">
          <Avatar>
            <AvatarFallback className="text-xs font-medium">{user.initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent contentDir="ltr" align="start" className="max-h-[80svh] w-[min(20rem,calc(100vw-1rem))] overflow-y-auto" onCloseAutoFocus={(event) => { if (elevateOpen) event.preventDefault(); }}>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">{user.name}</p>
            <p className="text-muted-foreground truncate text-xs">{user.email}</p>
            <p className="text-muted-foreground text-xs capitalize">Role: {user.role}</p>
          </div>
        </DropdownMenuLabel>
        <ProfileAccess close={() => setOpen(false)} elevate={() => { setOpen(false); setElevateOpen(true); }} />
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuCheckboxItem checked={enabled} onCheckedChange={setEnabled}>
            <Settings2Icon aria-hidden /> Manage
          </DropdownMenuCheckboxItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => { setOpen(false); setPanel('notifications'); }}><BellIcon />Notifications</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => { setOpen(false); setPanel('help'); }}><CircleHelpIcon />Help</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <SunMoonIcon /> Theme
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => setTheme(value as Theme)}
            >
              <DropdownMenuRadioItem value="light">
                <SunIcon className="size-4" /> Light
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">
                <MoonIcon className="size-4" /> Dark
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">
                <MonitorIcon className="size-4" /> System
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={signOut}>
          <LogOutIcon /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <Sheet open={panel !== null} onOpenChange={open => { if (!open) setPanel(null); }}>
      <SheetContent className="overflow-y-auto" onCloseAutoFocus={() => trigger.current?.focus()}>
        <SheetHeader><SheetTitle>{panel === 'notifications' ? 'Notifications' : 'Help'}</SheetTitle><SheetDescription>{panel === 'notifications' ? 'Your access requests and decisions.' : 'Using tenant and entity access.'}</SheetDescription></SheetHeader>
        <div className="space-y-4 p-4">
          {panel === 'notifications' && <ElevationRequests />}
          {panel === 'help' && <>
            <p>Use the Tenant and Entity controls in the header or account menu to choose where you work. Only workspaces you can access are available.</p>
            <p>My roles shows your active roles, roles you are eligible to request, and temporary elevations. Choose Elevate access to request 1–24 hours with a reason. Another authorized administrator must approve the request.</p>
            <p>The amber banner shows when temporary access expires. You can end it early from Elevate access. Notifications shows the current status of your access requests.</p>
          </>}
        </div>
      </SheetContent>
    </Sheet>
    {elevateOpen && <ElevateAccessDrawer open={elevateOpen} onOpenChange={setElevateOpen} onClose={() => trigger.current?.focus()} />}
    </>
  );
}
